import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const GREY_CONTAINER_WIDTH = 1010;
const DESKTOP_CARD_WIDTH = 281;
const DESKTOP_CARD_HEIGHT = 156;
const DESKTOP_GAP = 24;
const MAX_COLUMNS = 4;

const PartnerLogoGridCard = ({ card, index, navigate }) => {
  const isClickable = Boolean(card?.url);
  const Component = isClickable ? 'button' : 'div';

  return (
    <Box
      component={Component}
      type={isClickable ? 'button' : undefined}
      onClick={isClickable ? () => navigate(card.url) : undefined}
      aria-label={
        isClickable ? `View ${card.imageAltTitle || card.imgAltTag}` : undefined
      }
      sx={{
        width: `${DESKTOP_CARD_WIDTH}px`,
        height: `${DESKTOP_CARD_HEIGHT}px`,
        backgroundColor: '#FFFFFF',
        borderRadius: '34px',
        boxShadow:
          '0px 6px 16px 0px rgba(22, 47, 59, 0.12), 0px 1px 2px 0px rgba(162, 172, 177, 0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: '34px',
        py: '40px',
        boxSizing: 'border-box',
        appearance: 'none',
        border: 'none',
        cursor: isClickable ? 'pointer' : 'default',
        font: 'inherit',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': isClickable
          ? {
              transform: 'translateY(-2px)',
              boxShadow:
                '0px 10px 22px 0px rgba(22, 47, 59, 0.16), 0px 2px 6px 0px rgba(162, 172, 177, 0.16)',
            }
          : undefined,
      }}
    >
      {card?.logo && (
        <Image
          width={281}
          height={156}
          src={card.logo}
          alt={card.imgAltTag || `partner-logo-${index + 1}`}
          title={card.imageAltTitle || ''}
          loading="eager"
          sx={{
            maxWidth: '100%',
            maxHeight: '76px',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
};

const PartnerLogoGridComponent = ({ data }) => {
  const navigate = useNavigate();
  const { title, description, cards = [], component = 'h2' } = data || {};

  const desktopColumns = Math.min(Math.max(cards.length, 1), MAX_COLUMNS);
  const maxGridWidth =
    desktopColumns * DESKTOP_CARD_WIDTH + (desktopColumns - 1) * DESKTOP_GAP;

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',

        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          width: '100%',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            width: `${GREY_CONTAINER_WIDTH}px`,
            maxWidth: '100%',
            mx: 'auto',
            backgroundColor: '#E3E6E8',
            borderRadius: '48px',
            px: '32px',
            pt: '32px',
            pb: '32px',
            boxSizing: 'border-box',
            textAlign: 'center',
            overflow: 'visible',
          }}
        >
          <Typography
            component={component}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '16px',
              whiteSpace: 'pre-line',
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>

          {description && (
            <Box
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '1.5rem',
                lineHeight: '1.75rem',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                maxWidth: '820px',
                mx: 'auto',
                mb: '40px',
                textAlign: 'center',
                '& p': { m: 0, mb: '16px' },
                '& p:last-of-type': { mb: 0 },
                '& a': {
                  color: '#162F3B',
                  textDecoration: 'underline',
                  fontWeight: 400,
                },
                '& a:hover': {
                  opacity: 0.85,
                },
              }}
              dangerouslySetInnerHTML={{ __html: description || '' }}
            />
          )}

          {cards.length > 0 && (
            <Box
              sx={{
                width: `${maxGridWidth}px`,
                position: 'relative',
                left: '50%',
                transform: 'translateX(-50%)',
                mt: '40px',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: `${DESKTOP_GAP}px`,
              }}
            >
              {cards.map((card, index) => (
                <ScrollFadeWrapper
                  key={`${card?.imageAltTitle || card?.imgAltTag || 'card'}-${index}`}
                  delay={index * 0.06}
                >
                  <PartnerLogoGridCard
                    card={card}
                    index={index}
                    navigate={navigate}
                  />
                </ScrollFadeWrapper>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default PartnerLogoGridComponent;
